import { ValidatorFn, AbstractControl } from '@angular/forms';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import { environment } from 'src/environments/environment';
import { CampaignAdapter } from '../../adapters/campaign-adapter';

const MILISECOND_OF_DAY = 86400000;  //(24*60*60*1000) is milisecond of 1 day
const MILISECOND_OF_7_DAYS = 604800000;  //(7*24*60*60*1000) is miliseconds of 7 days

export function positiveNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        const isNotOk = Number(control.value) < 0;
        return isNotOk ? { nonPositive: { value: control.value } } : null;
    };
}

export function greaterThanMinimum(minimum: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        let isNotOk = false;
        isNotOk = control.value === null || control.value === undefined || +control.value < minimum;
        return isNotOk ? { greaterThanMinimum: { value: control.value } } : null;
    };
}

export function greaterThanZero(allowNullOrEmptyString: boolean = true): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        let isNotOk = false;
        if (allowNullOrEmptyString) {
            isNotOk = +control.value <= 0 && control.value !== null && control.value !== undefined && control.value !== '';
        } else {
            isNotOk = +control.value <= 0 || control.value === null || control.value === undefined;
        }
        return isNotOk ? { greaterThanZero: { value: control.value } } : null;
    };
}

export function nonZeroNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        const isNotOk = Number(control.value) <= 0;
        return isNotOk ? { nonPositive: { value: control.value } } : null;
    };
}

export function lessThanLimit(
    limit: number,
    allowNull: boolean = true
): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        let isNotOk = false;

        if (allowNull) {
            isNotOk = +control.value > limit && control.value !== null;
        } else {
            isNotOk = +control.value > limit || control.value === null;
        }
        return isNotOk
            ? { lessThanLimit: { value: control.value, limit: limit } }
            : null;
    };
}

export function languageChannelValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        let languageControl: AbstractControl = control.get('languages');
        let channelControl: AbstractControl = control.get('channels');
        return _.isEmpty(languageControl.value) || _.isEmpty(channelControl.value) ? { emptyLanguageChannel: true } : null;
    };
}

export function validateUnderCondition(
    validatorFn: ValidatorFn,
    conditionFn: () => boolean
): ValidatorFn {
    return (control: AbstractControl) => {
        if (!conditionFn()) {
            return null;
        }
        return validatorFn(control);
    };
}

export function dateValidator(campaignAdapter: CampaignAdapter, campaignId: number, gapBetweenStartDateAndToday: number = MILISECOND_OF_7_DAYS): ValidatorFn {
    let minStartDate = moment(new Date(new Date().getTime() + 7 * 86400000)).tz(environment.timezone, true).startOf("day").toDate().getTime();
    let maxEndDate = null;
    campaignAdapter.getAvailableFlightDateRange(campaignId).then((resp) => {
        if (resp) {
            if (resp.minStartDate && resp.minStartDate.getTime() > minStartDate) {
                minStartDate = moment(resp.minStartDate).tz(environment.timezone, true).startOf("day").toDate().getTime();
            }
            if (resp.maxEndDate) {
                maxEndDate = moment(resp.maxEndDate).tz(environment.timezone, true).endOf("day").toDate().getTime();
            }
        }
    });
    return (control: AbstractControl) => {
        let startDateControl: AbstractControl = control.get('startDate');
        let endDateControl: AbstractControl = control.get('endDate');
        if (startDateControl.touched || endDateControl.touched || startDateControl.value) {
            let startDate = startDateControl.value;
            let endDate = endDateControl.value;
            startDate = typeof startDate === "string" ? Date.parse(startDate) : startDate;
            endDate = typeof endDate === "string" ? Date.parse(endDate) : endDate;
            let currentDate = moment(new Date()).tz(environment.timezone, true).startOf("day").toDate().getTime();
            return startDate !== null && ((startDate - currentDate) < gapBetweenStartDateAndToday)
                ? { invalidStartDate: true } : startDate < endDate ? (endDate - startDate) >= MILISECOND_OF_DAY
                    ? startDate >= minStartDate && (maxEndDate === null || endDate <= maxEndDate) ? null : { invalidSelectedDate: true }
                    : { invalidDateRange: true } : { invalidDateRange: true };
        }

        return null;
    };
}

export function goalValidator(): ValidatorFn {
    return (control: AbstractControl) => {
        let goalTypeControl: AbstractControl = control.get('goalType');
        let autoSelectControl: AbstractControl = control.get('autoSelect');
        if (goalTypeControl.touched || autoSelectControl.touched) {
            let autoSelect = autoSelectControl.value || false;
            let goalType = goalTypeControl.value;
            if (!goalType && !autoSelect) {
                return { invalidGoal: true };
            }
        }
        return null;
    };
}

export function hasEndashOrEmDash(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        return control && control.value && /[-—]/.test(control.value) ? { dashNotSupportedInCampaignName: true } : null;
    };
}

export function creativeRequiredValidator(): ValidatorFn {
    return (control: AbstractControl) => {
        let creativesControl: AbstractControl = control.get('creatives');
        let produceCreativeControl: AbstractControl = control.get('produceCreative');
        if (!creativesControl.disabled && !produceCreativeControl.disabled) {
            if (_.isEmpty(creativesControl.value) && _.isEmpty(produceCreativeControl.value)) {
                return { creativesRequired: true };
            }
        }
        return null;
    };
}

export function optionValidator(options: Array<any>, mapOption = function (option) { return option }): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value || !options.length) return null;
        if (options.map(mapOption).includes(control.value)) {
            return null;
        }
        return { invalidOption: true };
    };
}

export function rejectedCreativeValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (control.value && control.value.find(creative => creative.creative_status === 'Rejected')) {
            return { rejectedCreative: true };
        }
        return null;
    };
}

export function uniqueValidator(exists: Array<any>): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value || !exists.length) return null;
        return exists.indexOf(control.value) !== -1 ? { alreadyExists: true } : null;
    };
}

function validFullYear(year, options: { minYear?: number, maxYear?: number } = {}) {
    year = parseInt(year);
    options.minYear = options.minYear || 1800;
    options.maxYear = options.maxYear || (new Date).getFullYear();
    return options.maxYear >= year && year >= options.minYear;
}

/**
 * validates UEN of businesses in Singapore
 * Source: https://gist.github.com/mervintankw/90d5660c6ab03a83ddf77fa8199a0e52
 * @param uen
 * @returns {boolean}
 */
function getInvalidUenMessage(uen) {
    var debug = !environment.production;
    const entityTypeIndicator = [
        'LP', 'LL', 'FC', 'PF', 'RF', 'MQ', 'MM', 'NB', 'CC', 'CS', 'MB', 'FM', 'GS', 'GA',
        'GB', 'DP', 'CP', 'NR', 'CM', 'CD', 'MD', 'HS', 'VH', 'CH', 'MH', 'CL', 'XL', 'CX',
        'RP', 'TU', 'TC', 'FB', 'FN', 'PA', 'PB', 'SS', 'MC', 'SM'
    ];

    if (debug) {
        console.log('(A) Businesses registered with ACRA');
        console.log('(B) Local companies registered with ACRA');
        console.log('(C) All other entities which will be issued new UEN');
    }

    // check that uen is not empty
    if (!uen || String(uen) === '') {
        if (debug) { console.log('UEN is empty'); }
        return 'UEN is empty';
    }

    // check if uen is 9 or 10 digits
    if (uen.length < 9 || uen.length > 10) {
        if (debug) { console.log('UEN is not 9 or 10 digits'); }
        return 'UEN is not 9 or 10 digits';
    }

    uen = uen.toUpperCase();
    var uenStrArray = uen.split('');

    // (A) Businesses registered with ACRA
    if (uenStrArray.length === 9) {
        // check that last character is a letter
        const lastChar = uenStrArray[uenStrArray.length - 1];
        if (lastChar < 'A' || 'Z' < lastChar) {
            if (debug) { console.log('(A) last character is not an alphabet'); }
            return 'last character is not an alphabet';
        }

        for (var i = 0; i < uenStrArray.length - 1; i++) {
            // check that first 8 letters are all numbers
            if (isNaN(uenStrArray[i])) {
                if (debug) { console.log('(A) there are non-numbers in 1st to 8th letters'); }
                return 'there are non-numbers in 1st to 8th letters';
            }
        }

        // (A) Businesses registered with ACRA (SUCCESS)
        if (debug) { console.log('valid (A) Businesses registered with ACRA'); }
        return false;
    }
    else if (uenStrArray.length === 10) {
        // check that last character is a letter
        const lastChar = uenStrArray[uenStrArray.length - 1];
        if (lastChar < 'A' || 'Z' < lastChar) {
            if (debug) { console.log('(B)(C) last character is not an alphabet'); }
            return 'last character is not an alphabet';
        }

        // (B) Local companies registered with ACRA
        if (!isNaN(uenStrArray[0]) && !isNaN(uenStrArray[1]) && !isNaN(uenStrArray[2]) && !isNaN(uenStrArray[3])) {
            // check that 5th to 9th letters are all numbers
            if (!isNaN(uenStrArray[4]) && !isNaN(uenStrArray[5]) && !isNaN(uenStrArray[6]) &&
                !isNaN(uenStrArray[7]) && !isNaN(uenStrArray[8])) {
                // (B) Local companies registered with ACRA (SUCCESS)
                if (debug) { console.log('valid (B) Local companies registered with ACRA'); }
                return false;
            } else {
                if (debug) { console.log('(B) there are non-numbers in 5th to 9th letters'); }
                return 'there are non-numbers in 5th to 9th letters';
            }
        }
        // (C) All other entities which will be issued new UEN
        else {
            // check that 1st letter is either T or S or R
            if (uenStrArray[0] !== 'T' && uenStrArray[0] !== 'S' && uenStrArray[0] !== 'R') {
                if (debug) { console.log('(C) 1st letter is incorrect'); }
                return '1st letter is incorrect';
            }

            // check that 2nd and 3rd letters are numbers only
            if (isNaN(uenStrArray[1]) || isNaN(uenStrArray[2])) {
                if (debug) { console.log('(C) 2nd and 3rd letter is incorrect'); }
                return '2nd and 3rd letter is incorrect';
            }

            // check that 4th letter is an alphabet
            if (!isNaN(uenStrArray[3])) {
                if (debug) { console.log('(C) 4th letter is not an alphabet'); }
                return '4th letter is not an alphabet';
            }

            // check entity-type indicator
            var entityTypeMatch = false,
                entityType = String(uenStrArray[3]) + String(uenStrArray[4]);
            for (var i = 0; i < entityTypeIndicator.length; i++) {
                if (String(entityTypeIndicator[i]) === String(entityType)) {
                    entityTypeMatch = true;
                }
            }
            if (!entityTypeMatch) {
                if (debug) { console.log('(C) entity-type indicator is invalid'); }
                return 'entity-type indicator is invalid';
            }

            // check that 6th to 9th letters are numbers only
            if (isNaN(uenStrArray[5]) || isNaN(uenStrArray[6]) || isNaN(uenStrArray[7]) || isNaN(uenStrArray[8])) {
                if (debug) { console.log('(C) 2nd and 3rd letter is incorrect'); }
                return '2nd and 3rd letter is incorrect';
            }

            // (C) All other entities which will be issued new UEN (SUCCESS)
            if (debug) { console.log('valid (C) All other entities which will be issued new UEN'); }
            return false;
        }
    }

    return 'Invalid UEN';
}

/**
 * Format based on https://www.uen.gov.sg/ueninternet/faces/pages/admin/aboutUEN.jspx
 *
 * ┌────────────────────────┬─────────────────────────────────────────────────┐
 * │ UEN Format             │ Issued To                                       │
 * ├────────────────────────┼─────────────────────────────────────────────────┤
 * │ nnnnnnnnX (9 digits)   │ Businesses registered with ACRA                 │
 * ├────────────────────────┼─────────────────────────────────────────────────┤
 * │ yyyynnnnnX (10 digits) │ Local companies registered with ACRA            │
 * ├────────────────────────┼─────────────────────────────────────────────────┤
 * │ TyyPQnnnnX (10 digits) │ All other entities which will be issued new UEN │
 * └────────────────────────┴─────────────────────────────────────────────────┘
 *
 * 'n' = a number
 * 'P'= a alphabetical letter
 * 'Q' = an alpha-numeric digit
 * 'X' = a check alphabet
 * 'PQ' = Entity-type
 * 'Tyy' / 'Syy' / 'yyyy'= year of issuance
 * 'T' represents '20', 'S' represents '19' and 'R' represents '18'.
 *     E.g. T08 means year 2008, S99 means year 1999 and R00 means year 1800
 */
export function uenValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) return null;

        const message = getInvalidUenMessage(control.value);
        if (!message) return null;
        return { invalidFormat: true, invalidMessage: message };

        // // Format: nnnnnnnnX
        // if (control.value.match(/^[0-9]{8}[A-z]$/g)) {
        //     return null;
        // }
        // // Format: yyyynnnnnX
        // if (control.value.match(/^[0-9]{9}[A-z]$/g)) {
        //     if (validFullYear(control.value.match(/^[0-9]{4}/)[0])) {
        //         return null;
        //     }
        // }
        // // Format: TyyPQnnnnX
        // /**
        //  * Note: Not checking entity-types in case there is any change
        //  */
        // if (control.value.match(/^[A-z][0-9]{2}[A-z]{2}[0-9]{4}[A-z]$/g)) {
        //     const year = control.value.match(/^[A-z][0-9]{2}/g)[0];
        //     if (validFullYear(year.replace('T', 20).replace('S', 19).replace('R', 18))) {
        //         return null;
        //     }
        // }
        // return { invalidFormat: true };
    }
}

/**
 * Format based on https://en.wikipedia.org/wiki/Telephone_numbers_in_Singapore
 *
 * ┌────────────────────────────────────────────────────────────────────────────────────────────┐
 * │ Number ranges                                                                              │
 * ├───────────┬────────────────────────────────────────────────────────────────────────────────┤
 * │ 3xxx xxxx │ Business and Corporate IP Telephony Services or VOIP line numbers.[4]          │
 * ├───────────┼────────────────────────────────────────────────────────────────────────────────┤
 * │ 6xxx xxxx │ Public Switched Telephone Network (PSTN) and Residential IP Telephony Services │
 * ├───────────┼────────────────────────────────────────────────────────────────────────────────┤
 * │ 8zxx xxxx │ Mobile, Data Services, New Numbers and Prepaid Numbers                         │
 * ├───────────┼────────────────────────────────────────────────────────────────────────────────┤
 * │ 9yxx xxxx │ Mobile, Data Services and Prepaid Numbers (Pagers Until May 2012)              │
 * └───────────┴────────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────┐
 * │ Special service numbers                          │
 * ├───────────────┬──────────────────────────────────┤
 * │ 800 xxx xxxx  │ Toll-free international services │
 * ├───────────────┼──────────────────────────────────┤
 * │ 1800 xxx xxxx │ Toll-free domestic services      │
 * ├───────────────┼──────────────────────────────────┤
 * │ 1900 xxx xxxx │ Premium rate services            │
 * └───────────────┴──────────────────────────────────┘
 *
 * 'x' denotes 0 to 9
 * 'y' denotes 0 to 8 only
 * 'z' denotes 1 to 9 only
 */
export function singaporePhoneNumberValidator(allowDashOrSpace: boolean = true, allowSpecialServiceNumber: boolean = false): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) return null;
        // Clean phone number if allow dash or space characters
        let phoneNumber = allowDashOrSpace ? control.value.replace(/[- ]/g, '') : control.value;

        // Format: 3xxx xxxx or 6xxx xxxx or 8zxx xxxx or 9yxx xxxx
        if (phoneNumber.match(/^(([36])[0-9]|8[1-9]|9[0-8])[0-9]{6}$/g)) {
            return null;
        }

        if (allowSpecialServiceNumber) {
            // Format: 800 xxx xxxx or 1800 xxx xxxx or 1900 xxx xxxx
            if (phoneNumber.match(/^(800|(18|19)00)[0-9]{7}$/g)) {
                return null;
            }

        }
        return { invalidPhoneNumber: true };
    };
}

/**
 * Format based on https://en.wikipedia.org/wiki/Postal_codes_in_Singapore
 *
 * 1. 6-digit numbers
 * 2. First 2 digit < 82 but not 00 or 74
 */
export function singaporePostcodeValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) return null;
        const postalSector = parseInt(control.value.slice(0, 2));
        if (control.value.match(/^\d{6}$/g) && postalSector > 0 && postalSector < 83 && postalSector !== 74) {
            return null;
        }
        return { invalidPostcode: true };
    };
}
